import type { MyFeedbackItem } from "@esi/shared";
import { MessageSquareText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "../i18n/format.ts";
import { ACCOUNT_PATH, Link } from "../router.tsx";
import { FeedbackStatus } from "./FeedbackStatus.tsx";
import { MyScreenshot } from "./MyScreenshot.tsx";
import { useMyFeedback } from "./queries.ts";

/**
 * "My feedback" (openspec change `in-app-feedback`, spec "Reporters follow their reports"): the
 * caller's own reports, newest first, with their type, time, description and status. A sub-page
 * of the account settings. Descriptions show as plain text, as written.
 */
export function MyFeedbackPage() {
  const { t } = useTranslation();
  const reports = useMyFeedback();
  const back = (
    <nav aria-label={t("project.breadcrumb")} className="text-sm">
      <Link
        href={ACCOUNT_PATH}
        className="text-fg-secondary underline-offset-4 hover:text-fg hover:underline"
      >
        {t("accountSettings.title")}
      </Link>
    </nav>
  );
  return (
    <Page width="narrow" className="gap-6">
      <PageHeader before={back} title={t("myFeedback.title")} lead={t("myFeedback.lead")} />
      {reports.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : reports.isError ? (
        <Notice tone="danger">{reports.error.message}</Notice>
      ) : reports.data.length === 0 ? (
        <EmptyState icon={MessageSquareText} title={t("myFeedback.none")}>
          {t("myFeedback.noneBody")}
        </EmptyState>
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {reports.data.map((report) => (
            <FeedbackRow key={report.id} report={report} />
          ))}
        </ul>
      )}
    </Page>
  );
}

function FeedbackRow({ report }: { report: MyFeedbackItem }) {
  const { t } = useTranslation();
  return (
    <li className="flex min-w-0 flex-col gap-2 py-4 first:pt-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm font-medium text-fg">{t(`feedback.types.${report.type}`)}</span>
        <time dateTime={report.createdAt} className="tabular text-xs text-fg-muted">
          {formatDateTime(report.createdAt)}
        </time>
        <FeedbackStatus report={report} className="ml-auto" />
      </div>
      <p className="whitespace-pre-wrap break-words text-sm text-fg-secondary">
        {report.description}
      </p>
      {report.reason ? (
        // The operator's reason for "Not planned" (feedback-reporter-status), as plain text.
        <p className="break-words text-sm text-fg">
          {t("myFeedback.reason", { reason: report.reason })}
        </p>
      ) : null}
      {report.screenshot ? <MyScreenshot reportId={report.id} /> : null}
    </li>
  );
}

export { statusLabel } from "./FeedbackStatus.tsx";
